// File path: /shell/src/components/ModuleSidebar.tsx
// Module Sidebar - Sidebar navigation for the active module

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { ModuleId } from '@core/config/moduleRegistry';
import type { ModuleNavigationItem } from '@core/types/module';

interface ModuleSidebarProps {
  navigationItems: ModuleNavigationItem[];
  moduleId: ModuleId | null;
  isSidebarCollapsed: boolean;
  moduleColor: string;
  onItemClick?: () => void;
}

const ModuleSidebar: React.FC<ModuleSidebarProps> = ({
  navigationItems,
  moduleId,
  isSidebarCollapsed,
  moduleColor,
  onItemClick,
}) => {
  const location = useLocation();

  // Check if a route is active
  const isActiveRoute = (href: string) => {
    if (!moduleId) return false;

    const fullPath = `/${moduleId}/${href}`.replace(/\/+/g, '/');
    return location.pathname === fullPath ||
      location.pathname.startsWith(fullPath + '/');
  };

  if (!moduleId || navigationItems.length === 0) {
    return (
      <nav className="flex-1 px-2 py-4">
        <div className="text-white/50 text-sm text-center py-4">
          {isSidebarCollapsed ? '' : 'Select a module to begin'}
        </div>
      </nav>
    );
  }

  return (
    <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
      {navigationItems.map((item) => {
        const isActive = isActiveRoute(item.href);
        const Icon = item.icon;

        return (
          <Link
            key={item.name}
            to={`/${moduleId}/${item.href}`.replace(/\/+/g, '/')}
            onClick={onItemClick}
            className={`
              group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors
              ${isActive
                ? 'bg-white/20 text-white'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
              }
            `}
            title={isSidebarCollapsed ? item.name : undefined}
          >
            <Icon
              className={`
                h-5 w-5 transition-colors
                ${isActive ? 'text-white' : 'text-white/60 group-hover:text-white'}
                ${isSidebarCollapsed ? 'mx-auto' : 'mr-3'}
              `}
            />
            {!isSidebarCollapsed && (
              <>
                <span className="flex-1">{item.name}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-white/20 rounded-full">
                    {item.badge}
                  </span>
                )}
              </>
            )}
          </Link>
        );
      })}
    </nav>
  );
};

export default ModuleSidebar;
